'use client';

import { c as _c } from "react/compiler-runtime";
import type { PullRequestDemo } from '@/lib/code-sessions/pull-request-demo/demo-state';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchPrV2Demo, rerecordPrV2Demo } from './fetch-pr-v2';
import { prV2DemoRerecordMutationKey } from './mutation-keys';
import { PR_V2_STANDARD_STALE_TIME_MS } from './query-config';
import { prV2DemoQueryKey } from './query-keys';
import { codeSessionsAccessOptions } from '@/data/code-sessions/queries';
import { isPullRequestDemoInProgress } from '@/lib/code-sessions/pull-request-demo/demo-state';

/** How often the page discovers a demo or reads one cubic is still filming; a take runs for minutes. */
const DEMO_REFETCH_MS = 15_000;

/**
 * The demo as it stands once a new take is asked for: a video updating, or a take recording
 * where the last turns filmed nothing.
 */
function demoAwaitingTake(demo: PullRequestDemo): PullRequestDemo {
  switch (demo.state) {
    case 'recording':
      return demo;
    case 'ready':
      return {
        ...demo,
        updating: true
      };
    case 'no_demo':
    case 'failed':
      return {
        state: 'recording',
        sessionId: demo.sessionId
      };
  }
}

/**
 * The demo cubic filmed of the pull request. Only installations with /code run demo sessions, so
 * the request waits for the viewer's /code installations, which the browser keeps, and is never
 * sent for any other installation.
 */
export function usePrV2Demo(owner, repo, number, t0) {
  const $ = _c(31);
  const {
    installationId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = codeSessionsAccessOptions();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== enabled) {
    t3 = {
      ...t2,
      enabled
    };
    $[1] = enabled;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: codeInstallationIds,
    isLoading: isAccessLoading
  } = useQuery(t3);
  let t4;
  if ($[3] !== codeInstallationIds || $[4] !== installationId) {
    t4 = installationId !== null && Boolean(codeInstallationIds?.includes(installationId));
    $[3] = codeInstallationIds;
    $[4] = installationId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const hasCode = t4;
  let t5;
  if ($[6] !== number || $[7] !== owner || $[8] !== repo) {
    t5 = prV2DemoQueryKey(owner, repo, number);
    $[6] = number;
    $[7] = owner;
    $[8] = repo;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== number || $[11] !== owner || $[12] !== repo) {
    t6 = t7 => {
      const {
        signal
      } = t7;
      return fetchPrV2Demo(owner, repo, number, signal);
    };
    $[10] = number;
    $[11] = owner;
    $[12] = repo;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = enabled && hasCode;
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7,
      staleTime: PR_V2_STANDARD_STALE_TIME_MS,
      refetchInterval: _temp
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const query_0 = useQuery(t8);
  let t9;
  if ($[18] !== number || $[19] !== owner || $[20] !== repo) {
    t9 = prV2DemoRerecordMutationKey(owner, repo, number);
    $[18] = number;
    $[19] = owner;
    $[20] = repo;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = {
      mutationKey: t9
    };
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const isRerecording = useIsMutating(t10) > 0;
  let t11;
  if ($[24] !== isRerecording || $[25] !== query_0.data) {
    t11 = isRerecording && query_0.data?.demo ? {
      demo: demoAwaitingTake(query_0.data.demo)
    } : query_0.data;
    $[24] = isRerecording;
    $[25] = query_0.data;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const data = t11;
  let t12;
  if ($[27] !== data || $[28] !== isAccessLoading || $[29] !== query_0) {
    t12 = {
      ...query_0,
      data,
      isAccessLoading
    };
    $[27] = data;
    $[28] = isAccessLoading;
    $[29] = query_0;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}

/**
 * Asks cubic for a new take of the pull request's demo on its latest commit. Every read of the
 * demo shows it awaiting the take until cubic answers (`usePrV2Demo`). The demo cubic answers
 * with replaces the cached one, and the demo query keeps reading it until the take is done.
 */
function _temp(query) {
  return query.state.data?.demo === null || isPullRequestDemoInProgress(query.state.data?.demo) ? DEMO_REFETCH_MS : false;
}
export function useRerecordPrV2Demo(owner, repo, number) {
  const $ = _c(23);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2DemoQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const queryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = prV2DemoRerecordMutationKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== number || $[9] !== owner || $[10] !== repo) {
    t2 = () => rerecordPrV2Demo(owner, repo, number);
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== queryClient || $[13] !== queryKey) {
    t3 = response => {
      queryClient.setQueryData(queryKey, response);
      toast.success("Re-record started");
    };
    $[12] = queryClient;
    $[13] = queryKey;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== queryClient || $[16] !== queryKey) {
    t4 = () => {
      queryClient.invalidateQueries({
        queryKey,
        exact: true
      });
    };
    $[15] = queryClient;
    $[16] = queryKey;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t5 = {
      mutationKey: t1,
      mutationFn: t2,
      onSuccess: t3,
      onError: _temp2,
      onSettled: t4
    };
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return useMutation(t5);
}
function _temp2(error) {
  toast.error(error.message);
}